import { ProductLoading } from "@/components/product/ProductLoading";
import { useRequireLandlordVerification } from "@/hooks/useRequireLandlordVerification";
import { RUN_NOW } from "@/pages/Landlord/Dashboard/identityGate";
import { dashboardSeatOf } from "@/utils/dashboardSeat";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense, useEffect, type ReactNode } from "react";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router";
import { AuthProvider } from "./components/contexts/AuthContext/AuthContext";
import { useAuth } from "./components/contexts/AuthContext/UseAuth";
import { IdentityProvider } from "./components/contexts/IdentityContext/IdentityContext";
import { LoadingScreen } from "./components/LoadingScreen";
import {
  pageInOwnWorkspace,
  workspaceOf,
} from "./components/navigation/workspaceTwins";
import ProductAnalytics from "./components/ProductAnalytics";
import { ErrorView } from "./components/ui/ErrorView";
import { NotFoundView } from "./components/ui/NotFoundView";
import { Toaster } from "./components/ui/shadcn/sonner";
import ReportsRedirect from "./pages/Shared/Reports/ReportsRedirect";
import ReportsWorkspace from "./pages/Shared/Reports/ReportsWorkspace";
import { UserType } from "./types/IdentityTypes";
import type { JobViewer } from "./types/JobTypes";

// Eagerly load authentication and entry point pages
import AgentInvitePage from "./pages/Agent/Invite";
import LoginCallbackPage from "./pages/Auth/LoginCallback";
import LogoutCallbackPage from "./pages/Auth/LogoutCallback";
import LoginErrorPage from "./pages/Identity/LoginError";
import RequestPasswordResetPage from "./pages/Identity/RequestPasswordReset";
import ResetPasswordPage from "./pages/Identity/ResetPassword";
import ResetPasswordSuccessPage from "./pages/Identity/ResetPasswordSuccess";
import SignInPage from "./pages/Identity/SignIn";
import SignOutPage from "./pages/Identity/SignOut";
import SignUpPage from "./pages/Identity/SignUp";
import VerifyEmailPage from "./pages/Identity/VerifyEmail";
import SelectExperiencePage from "./pages/SelectExperience/SelectExperiencePage";
import TernReturnPage from "./pages/Shared/TernReturnPage";

const PropertyIntakePage = lazy(
  () => import("./pages/Shared/PropertyIntakePage"),
);
const RenewalDraftPreviewPage = lazy(
  () => import("./pages/Shared/RenewalDraftPreviewPage"),
);
const PropertyIntakePageRoute = ({ purpose }: { purpose?: "tenancy" }) => (
  <Lazy>
    <PropertyIntakePage purpose={purpose} />
  </Lazy>
);

const queryClient = new QueryClient();

// Keep lazy component identities stable across location updates. Declaring
// them during render suspends/remounts the current page on query navigation.
const Lazy = ({ children }: { children: ReactNode }) => (
  <Suspense fallback={<LoadingScreen />}>{children}</Suspense>
);

const PropertiesPageComponent = lazy(
  () => import("./pages/Agent/Properties/PropertiesPage/PropertiesPage"),
);
const PropertiesPageRoute = () => (
  <Lazy>
    <PropertiesPageComponent />
  </Lazy>
);

const PropertyDetailsPageComponent = lazy(
  () => import("./pages/Agent/Properties/PropertyDetails/PropertyDetailsPage"),
);
const PropertyDetailsPageRoute = () => (
  <Lazy>
    <PropertyDetailsPageComponent />
  </Lazy>
);

const AgentDashboardPageComponent = lazy(
  () => import("./pages/Agent/Dashboard/AgentDashboard"),
);
const AgentDashboardRoute = () => {
  const seat = dashboardSeatOf(useAuth().user?.profile?.UserType);
  return (
    <Lazy>
      {seat === "manager" ? (
        <PortfolioDashboardPageComponent identityGate={RUN_NOW} />
      ) : (
        <AgentDashboardPageComponent />
      )}
    </Lazy>
  );
};

const AgentPeoplePageComponent = lazy(
  () => import("./pages/Agent/People/AgentPeople"),
);
const AgentPeopleRoute = () => (
  <Lazy>
    <AgentPeoplePageComponent />
  </Lazy>
);

const ManageAccessPageComponent = lazy(
  () => import("./pages/Agent/ManageAccess/ManageAccessPage"),
);
const ManageAccessRoute = () => (
  <Lazy>
    <ManageAccessPageComponent />
  </Lazy>
);

const PmConsolePageComponent = lazy(() => import("./pages/PM/PmConsole"));
const PmConsoleRoute = () => (
  <Lazy>
    <PmConsolePageComponent />
  </Lazy>
);

const LandlordProfilePageComponent = lazy(
  () => import("./pages/Landlord/LandlordProfilePage"),
);
const LandlordProfilePageRoute = () => (
  <Lazy>
    <LandlordProfilePageComponent />
  </Lazy>
);

const AgentProfilePageComponent = lazy(
  () => import("./pages/Agent/ProfilePage"),
);
const AgentProfilePageRoute = () => (
  <Lazy>
    <AgentProfilePageComponent />
  </Lazy>
);

const AgentLayoutPageComponent = lazy(
  () => import("./pages/Agent/AgentLayout"),
);
const AgentLayoutRoute = () => (
  <Lazy>
    <AgentLayoutPageComponent />
  </Lazy>
);

const AgentBillingPageComponent = lazy(
  () => import("./pages/Agent/Billing/AgentBillingPage"),
);
const AgentBillingRoute = () => (
  <Lazy>
    <AgentBillingPageComponent />
  </Lazy>
);

const LandlordBillingPageComponent = lazy(
  () => import("./pages/Landlord/Billing/LandlordBillingPage"),
);
const LandlordBillingRoute = () => (
  <Lazy>
    <LandlordBillingPageComponent />
  </Lazy>
);

const BillingSuccessPageComponent = lazy(
  () => import("./pages/Shared/Billing/BillingSuccessPage"),
);
const BillingSuccessRoute = ({ billingPath }: { billingPath: string }) => (
  <Lazy>
    <BillingSuccessPageComponent billingPath={billingPath} />
  </Lazy>
);

const BillingCancelPageComponent = lazy(
  () => import("./pages/Shared/Billing/BillingCancelPage"),
);
const BillingCancelRoute = ({ billingPath }: { billingPath: string }) => (
  <Lazy>
    <BillingCancelPageComponent billingPath={billingPath} />
  </Lazy>
);

const PropertyLeadsTabPageComponent = lazy(
  () => import("./pages/Agent/Properties/PropertyDetails/LeadsTab"),
);
const PropertyLeadsTabRoute = () => (
  <Lazy>
    <PropertyLeadsTabPageComponent />
  </Lazy>
);

const PropertyDetailsTabPageComponent = lazy(
  () =>
    import(
      "./pages/Agent/Properties/PropertyDetails/PropertyDetailsTab/PropertyDetailsTab"
    ),
);
const PropertyDetailsTabRoute = () => (
  <Lazy>
    <PropertyDetailsTabPageComponent />
  </Lazy>
);

const PropertyMoneyTabPageComponent = lazy(
  () => import("./pages/Agent/Properties/PropertyDetails/MoneyTab"),
);
const PropertyMoneyTabRoute = () => (
  <Lazy>
    <PropertyMoneyTabPageComponent />
  </Lazy>
);

const PortfolioDashboardPageComponent = lazy(
  () => import("./pages/Landlord/Dashboard/PortfolioDashboardPage"),
);
const LandlordDashboardRoute = () => {
  const identityGate = useRequireLandlordVerification();
  return (
    <Lazy>
      <PortfolioDashboardPageComponent identityGate={identityGate} />
    </Lazy>
  );
};

const LandlordLayoutPageComponent = lazy(
  () => import("./pages/Landlord/LandlordLayout"),
);
const LandlordLayoutRoute = () => (
  <Lazy>
    <LandlordLayoutPageComponent />
  </Lazy>
);

const LandlordPropertiesPageComponent = lazy(
  () => import("./pages/Landlord/Properties/LandlordPropertiesPage"),
);
const LandlordPropertiesPageRoute = () => (
  <Lazy>
    <LandlordPropertiesPageComponent />
  </Lazy>
);

const LandlordPropertyDetailsPageComponent = lazy(
  () => import("./pages/Landlord/Properties/LandlordPropertyDetailsPage"),
);
const LandlordPropertyDetailsPageRoute = () => (
  <Lazy>
    <LandlordPropertyDetailsPageComponent />
  </Lazy>
);

const LandlordLeadsTabPageComponent = lazy(
  () => import("./pages/Landlord/Properties/PropertyDetails/LandlordLeadsTab"),
);
const LandlordLeadsTabRoute = () => (
  <Lazy>
    <LandlordLeadsTabPageComponent />
  </Lazy>
);

const LandlordPeoplePageComponent = lazy(
  () => import("./pages/Landlord/People/LandlordPeople"),
);
const LandlordPeoplePageRoute = () => (
  <Lazy>
    <LandlordPeoplePageComponent />
  </Lazy>
);

const LandlordMoneyPageComponent = lazy(
  () => import("./pages/Landlord/Money/LandlordMoneyPage"),
);
const LandlordMoneyPageRoute = () => (
  <Lazy>
    <LandlordMoneyPageComponent />
  </Lazy>
);

// An addendum Hazel composed: its values filled and approved here (design v1).
const AddendumPageComponent = lazy(
  () => import("./pages/Shared/Addenda/AddendumPage"),
);
const AddendumPageRoute = () => (
  <Lazy>
    <AddendumPageComponent />
  </Lazy>
);

// `/addenda/:id`, the link Hazel sends: one link for whoever the addendum is
// for, opened in the workspace of the account that signs in (the page is the
// same in both). Signed out, it asks for a sign-in with no workspace chosen,
// as the reports links do, and comes back here.
const AddendumLinkRoute = () => {
  const { isAuthenticated, user, login } = useAuth();
  const { id = "" } = useParams();
  const { pathname, search, hash } = useLocation();
  const destination = pathname + search + hash;
  useEffect(() => {
    if (!isAuthenticated) void login(destination);
  }, [isAuthenticated, login, destination]);
  if (!isAuthenticated) return <LoadingScreen />;
  const workspace = workspaceOf(user?.profile?.UserType);
  return (
    <Navigate
      to={workspace ? `${workspace}/addenda/${id}${search}${hash}` : "/"}
      replace
    />
  );
};

/** Approved addendum review has the compact chrome, on both existing workspace URLs. */
const AddendumReviewRoute = () => {
  const { isAuthenticated, user, login } = useAuth();
  const { id = "" } = useParams();
  const location = useLocation();
  useEffect(() => {
    if (!isAuthenticated)
      void login(`/addenda/${id}${location.search}${location.hash}`);
  }, [isAuthenticated, login, id, location.search, location.hash]);
  if (!isAuthenticated) return <LoadingScreen />;
  const workspace = workspaceOf(user?.profile.UserType);
  if (!workspace) return <Navigate to="/" replace />;
  const own = `${workspace}/addenda/${id}${location.search}${location.hash}`;
  if (location.pathname + location.search + location.hash !== own)
    return <Navigate to={own} replace />;
  return <AddendumPageRoute />;
};

const ManagerMoneyPageComponent = lazy(
  () => import("./pages/Agent/Money/ManagerMoneyPage"),
);
const ManagerMoneyPageRoute = () => (
  <Lazy>
    <ManagerMoneyPageComponent />
  </Lazy>
);

// End-user chat is WhatsApp-only; the original chat implementation is retained.
const LegacyOfferPage = lazy(
  () => import("./pages/Shared/LegacyNotifications/LegacyOfferPage"),
);
const LegacyOfferRoute = () => (
  <Lazy>
    <LegacyOfferPage />
  </Lazy>
);
const LegacyEjariPage = lazy(
  () => import("./pages/Shared/LegacyNotifications/LegacyEjariPage"),
);
const LegacyEjariRoute = () => (
  <Lazy>
    <LegacyEjariPage />
  </Lazy>
);
const LegacyContactRedirect = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  if (!params.has("tab")) params.set("tab", "renters");
  return <Navigate to={`/agent/people?${params}${location.hash}`} replace />;
};
const LegacyFinancialsRedirect = () => {
  const location = useLocation();
  return (
    <Navigate
      to={`/landlord/money${location.search}${location.hash}`}
      replace
    />
  );
};

const HazelWhatsAppPageComponent = lazy(
  () => import("./pages/Shared/HazelWhatsAppPage"),
);
const HazelWhatsAppRoute = () => (
  <Lazy>
    <HazelWhatsAppPageComponent />
  </Lazy>
);

const RenterSunsetPageComponent = lazy(
  () => import("./pages/Shared/RenterSunsetPage"),
);
const RenterSunsetRoute = () => (
  <Lazy>
    <RenterSunsetPageComponent />
  </Lazy>
);

const JobsListPageComponent = lazy(
  () => import("./pages/Shared/Jobs/JobsListPage"),
);
const JobsListRoute = ({
  viewer,
  basePath,
}: {
  viewer: JobViewer;
  basePath: string;
}) => (
  <Lazy>
    <JobsListPageComponent viewer={viewer} basePath={basePath} />
  </Lazy>
);

const JobDetailPageComponent = lazy(
  () => import("./pages/Shared/Jobs/JobDetailPage"),
);
const JobDetailRoute = ({
  viewer,
  basePath,
}: {
  viewer: JobViewer;
  basePath: string;
}) => (
  <Lazy>
    <JobDetailPageComponent viewer={viewer} basePath={basePath} />
  </Lazy>
);

const InspectionsListPageComponent = lazy(
  () => import("./pages/Shared/Inspections/InspectionsListPage"),
);
const InspectionsListRoute = ({
  viewer,
  basePath,
}: {
  viewer: JobViewer;
  basePath: string;
}) => (
  <Lazy>
    <InspectionsListPageComponent viewer={viewer} basePath={basePath} />
  </Lazy>
);

const InspectionDetailPageComponent = lazy(
  () => import("./pages/Shared/Inspections/InspectionDetailPage"),
);
const InspectionDetailRoute = ({
  viewer,
  basePath,
}: {
  viewer: JobViewer;
  basePath: string;
}) => (
  <Lazy>
    <InspectionDetailPageComponent viewer={viewer} basePath={basePath} />
  </Lazy>
);

const SignByLinkPage = lazy(() => import("./pages/Shared/SignByLinkPage"));
const OwnerInvitePage = lazy(() => import("./pages/Landlord/OwnerInvitePage"));
const PayEjariFeePage = lazy(() => import("./pages/Shared/PayEjariFeePage"));

const SharedFinancialScore = lazy(
  () => import("./pages/Renter/Financials/SharedFinancialScore"),
);

const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

const BrokerWorkspaceRoute = lazy(
  () => import("./pages/BrokerWorkspace/BrokerWorkspace"),
);
// The review links render before account onboarding and analytics, but their
// pages read the session and the API like any other page.
const ReviewLinkProviders = ({ children }: { children: ReactNode }) => (
  <Sentry.ErrorBoundary
    fallback={
      <div className="h-[100dvh] w-full">
        <ErrorView />
      </div>
    }
  >
    <QueryClientProvider client={queryClient}>
      <AuthProvider loadingElement={<LoadingScreen />}>{children}</AuthProvider>
    </QueryClientProvider>
  </Sentry.ErrorBoundary>
);

export const App = () => {
  const location = useLocation();
  // The WhatsApp preview uses an expiring capability in the URL fragment.
  // Keep the capability review outside account onboarding and analytics.
  // The page resolves signed-in reads and returns here after edit/approve sign-in.
  if (location.pathname.startsWith("/renewal-drafts/"))
    return (
      <ReviewLinkProviders>
        <Lazy>
          <Routes>
            <Route
              path="/renewal-drafts/:draftId"
              element={<RenewalDraftPreviewPage />}
            />
          </Routes>
        </Lazy>
      </ReviewLinkProviders>
    );
  if (/^\/(?:landlord\/|agent\/)?addenda\/[^/]+\/?$/.test(location.pathname))
    return (
      <ReviewLinkProviders>
        <Routes>
          <Route path="/addenda/:id" element={<AddendumLinkRoute />} />
          <Route
            path="/landlord/addenda/:id"
            element={<AddendumReviewRoute />}
          />
          <Route path="/agent/addenda/:id" element={<AddendumReviewRoute />} />
        </Routes>
      </ReviewLinkProviders>
    );
  if (location.pathname === "/broker-workspace")
    return (
      <Suspense fallback={<ProductLoading />}>
        <BrokerWorkspaceRoute />
      </Suspense>
    );
  return (
    <Sentry.ErrorBoundary
      fallback={
        <div className="h-[100dvh] w-full">
          <ErrorView />
        </div>
      }
    >
      <QueryClientProvider client={queryClient}>
        <IdentityProvider loadingElement={<LoadingScreen />}>
          <AuthProvider loadingElement={<LoadingScreen />}>
            <ProductAnalytics />
            <SentryRoutes>
              <Route path="/" element={<IndexRoute />} />
              <Route
                path="/payments/tern/return"
                element={<TernReturnPage />}
              />
              <Route
                path="/signin"
                element={
                  <PublicOnlyRoute>
                    <SignInPage />
                  </PublicOnlyRoute>
                }
              />
              <Route path="/signout" element={<SignOutPage />} />
              <Route
                path="/signin-error"
                element={
                  <PublicOnlyRoute>
                    <LoginErrorPage />
                  </PublicOnlyRoute>
                }
              />
              <Route
                path="/signup"
                element={
                  <PublicOnlyRoute>
                    <SignUpPage />
                  </PublicOnlyRoute>
                }
              />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route
                path="/request-password-reset"
                element={<RequestPasswordResetPage />}
              />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route
                path="/reset-password-success"
                element={<ResetPasswordSuccessPage />}
              />
              {/* PM console — the agency back-office (Hazel conversations +
                  stack control plane). AgencyManager reaches their own
                  agency; platform Admin may select any registered agency via
                  ?agency=<slug>. Server-side the Core App re-gates by role +
                  the curated HazelPm:Agencies registry. */}
              <Route
                path="/pm"
                element={
                  <ProtectedRoute
                    expectedUserTypes={[UserType.AgencyManager, UserType.Admin]}
                    loginUserType={UserType.AgencyManager}
                  >
                    <PmConsoleRoute />
                  </ProtectedRoute>
                }
              />
              <Route element={<ReportsWorkspace />}>
                <Route path="/reports" element={<ReportsPageRoute />} />
                <Route
                  path="/reports/:reportId"
                  element={<AppReportPageRoute />}
                />
                <Route path="/views/:viewId" element={<SavedViewPageRoute />} />
              </Route>
              {/* Previously delivered links keep the signed-in experience. */}
              <Route path="/landlord/reports" element={<ReportsRedirect />} />
              <Route
                path="/landlord/reports/:reportId"
                element={<ReportsRedirect />}
              />
              <Route
                path="/landlord/views/:viewId"
                element={<ReportsRedirect />}
              />
              <Route path="/addenda/:id" element={<AddendumLinkRoute />} />
              <Route
                path="/agent"
                element={
                  <ProtectedRoute
                    // One shared management surface (2026-09-01): the persona
                    // Manager rides the agent-side pages, scoped server-side
                    // by delegations. Agency-only chrome stays gated by
                    // profile.isAgencyManager and backend policies.
                    expectedUserTypes={[
                      UserType.Agent,
                      UserType.AgencyManager,
                      UserType.Manager,
                    ]}
                    loginUserType={UserType.Agent}
                  >
                    <AgentLayoutRoute />
                  </ProtectedRoute>
                }
              >
                <Route index element={<AgentDashboardRoute />} />
                <Route path="dashboard" element={<AgentDashboardRoute />} />
                <Route path="people" element={<AgentPeopleRoute />} />
                {/* Compatibility for notifications delivered before the workspace changed. */}
                <Route path="offers/:offerId" element={<LegacyOfferRoute />} />
                <Route path="contact" element={<LegacyContactRedirect />} />
                <Route path="ejari" element={<LegacyEjariRoute />} />

                <Route path="money" element={<ManagerMoneyPageRoute />} />
                <Route path="addenda/:id" element={<AddendumPageRoute />} />
                <Route path="chat" element={<HazelWhatsAppRoute />} />
                <Route path="manage-access" element={<ManageAccessRoute />} />
                <Route path="properties">
                  <Route path="add" element={<PropertyIntakePageRoute />} />
                  <Route
                    path=":propertyId/documents/add"
                    element={<PropertyIntakePageRoute />}
                  />
                  <Route
                    path=":propertyId/tenancy/add"
                    element={<PropertyIntakePageRoute purpose="tenancy" />}
                  />
                  <Route path="" element={<PropertiesPageRoute />} />
                  <Route
                    path=":propertyId"
                    element={<PropertyDetailsPageRoute />}
                  >
                    <Route
                      path="leads"
                      index
                      element={<PropertyLeadsTabRoute />}
                    />
                    <Route
                      path="details"
                      element={<PropertyDetailsTabRoute />}
                    />
                    <Route path="money" element={<PropertyMoneyTabRoute />} />
                    <Route path="documents" element={null} />
                  </Route>
                </Route>
                <Route path="profile" element={<AgentProfilePageRoute />} />
                <Route path="billing" element={<AgentBillingRoute />} />
                <Route
                  path="billing/success"
                  element={<BillingSuccessRoute billingPath="/agent/billing" />}
                />
                <Route
                  path="billing/cancel"
                  element={<BillingCancelRoute billingPath="/agent/billing" />}
                />
                <Route path="jobs">
                  <Route
                    index
                    element={
                      <JobsListRoute viewer="agent" basePath="/agent/jobs" />
                    }
                  />
                  <Route
                    path=":jobId"
                    element={
                      <JobDetailRoute viewer="agent" basePath="/agent/jobs" />
                    }
                  />
                </Route>
                <Route path="inspections">
                  <Route
                    index
                    element={
                      <InspectionsListRoute
                        viewer="agent"
                        basePath="/agent/inspections"
                      />
                    }
                  />
                  <Route
                    path=":inspectionId"
                    element={
                      <InspectionDetailRoute
                        viewer="agent"
                        basePath="/agent/inspections"
                      />
                    }
                  />
                </Route>
              </Route>

              {/* Renter login retired (2026-09-01): stale renter invitation
                  links land on the sunset page, not a signup. */}
              <Route
                path="invite/:invitationId"
                element={<RenterSunsetRoute />}
              />
              <Route path="process-invite" element={<RenterSunsetRoute />} />
              <Route
                path="agent-invite/:invitationId"
                element={<AgentInvitePage />}
              />
              {/* An owner without an account invited to join and claim their
                  property: details are public, accepting needs a sign-in. */}
              <Route
                path="owner-invite/:reference"
                element={
                  <Lazy>
                    <OwnerInvitePage />
                  </Lazy>
                }
              />
              {/* Renter login retired (2026-09-01): renters live on
                  WhatsApp with Hazel and the journey deep link. Every old
                  renter route lands on the sunset page. The renter pages stay
                  in the tree, unmounted. */}
              <Route path="/tenant" element={<RenterSunsetRoute />} />
              <Route path="/tenant/*" element={<RenterSunsetRoute />} />
              <Route
                path="/landlord"
                element={
                  <ProtectedRoute
                    expectedUserTypes={[UserType.Landlord]}
                    loginUserType={UserType.Landlord}
                  >
                    <LandlordLayoutRoute />
                  </ProtectedRoute>
                }
              >
                <Route path="home" index element={<LandlordDashboardRoute />} />
                <Route path="properties">
                  <Route path="add" element={<PropertyIntakePageRoute />} />
                  <Route
                    path=":propertyId/documents/add"
                    element={<PropertyIntakePageRoute />}
                  />
                  <Route
                    path=":propertyId/tenancy/add"
                    element={<PropertyIntakePageRoute purpose="tenancy" />}
                  />
                  <Route index element={<LandlordPropertiesPageRoute />} />
                  {/* A signing invite's link: opens that offer's signing on the list. */}
                  <Route
                    path="sign/:offerId"
                    element={<LandlordPropertiesPageRoute />}
                  />
                  <Route
                    path=":propertyId"
                    element={<LandlordPropertyDetailsPageRoute />}
                  >
                    <Route path="details" element={null} />
                    <Route path="money" element={null} />
                    <Route path="documents" element={null} />
                    <Route path="leads" element={<LandlordLeadsTabRoute />} />
                  </Route>
                </Route>
                <Route path="people" element={<LandlordPeoplePageRoute />} />
                {/* Compatibility for notifications delivered before the workspace changed. */}
                <Route path="offers/:offerId" element={<LegacyOfferRoute />} />
                <Route
                  path="financials"
                  element={<LegacyFinancialsRedirect />}
                />

                <Route path="money" element={<LandlordMoneyPageRoute />} />
                <Route path="addenda/:id" element={<AddendumPageRoute />} />
                <Route path="profile" element={<LandlordProfilePageRoute />} />
                <Route path="chat" element={<HazelWhatsAppRoute />} />
                <Route path="billing" element={<LandlordBillingRoute />} />
                <Route
                  path="billing/success"
                  element={
                    <BillingSuccessRoute billingPath="/landlord/billing" />
                  }
                />
                <Route
                  path="billing/cancel"
                  element={
                    <BillingCancelRoute billingPath="/landlord/billing" />
                  }
                />
                <Route path="jobs">
                  <Route
                    index
                    element={
                      <JobsListRoute
                        viewer="landlord"
                        basePath="/landlord/jobs"
                      />
                    }
                  />
                  <Route
                    path=":jobId"
                    element={
                      <JobDetailRoute
                        viewer="landlord"
                        basePath="/landlord/jobs"
                      />
                    }
                  />
                </Route>
                <Route path="inspections">
                  <Route
                    index
                    element={
                      <InspectionsListRoute
                        viewer="landlord"
                        basePath="/landlord/inspections"
                      />
                    }
                  />
                  <Route
                    path=":inspectionId"
                    element={
                      <InspectionDetailRoute
                        viewer="landlord"
                        basePath="/landlord/inspections"
                      />
                    }
                  />
                </Route>
              </Route>
              {/* The manager workspace moved onto the shared management
                  surface under /agent (2026-09-01). Old links, bookmarks and
                  push notifications keep working via path rewrite. */}
              <Route path="/manager/*" element={<ManagerPathRedirect />} />
              <Route path="/manager" element={<ManagerPathRedirect />} />
              <Route path="/callback" element={<LoginCallbackPage />} />
              <Route path="/logout-callback" element={<LogoutCallbackPage />} />

              <Route
                path="/user/select-role"
                element={<SelectExperiencePage />}
              />

              {/* Public routes */}
              {/* The renter's public signing page — the reference in the URL
                  is the credential; no session required. */}
              <Route
                path="/sign/:reference"
                element={
                  <Lazy>
                    <SignByLinkPage />
                  </Lazy>
                }
              />
              {/* The tenant's Ejari fee link, on the same reference: it opens
                  a fresh Stripe Checkout each time. */}
              <Route
                path="/pay/:reference"
                element={
                  <Lazy>
                    <PayEjariFeePage />
                  </Lazy>
                }
              />

              <Route
                path="/financial-score/:shareToken"
                element={
                  <Lazy>
                    <SharedFinancialScore />
                  </Lazy>
                }
              />

              <Route path="*" element={<NotFoundView />} />
            </SentryRoutes>
          </AuthProvider>
        </IdentityProvider>
        <Toaster />
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
};

// /manager/* → the shared surface's equivalent page. "home" and
// "maintenance" had no same-named /agent route; everything else maps 1:1
// (properties, people, profile and their subpaths).
const ManagerPathRedirect = () => {
  const { pathname, search, hash } = useLocation();
  let target = pathname.replace(/^\/manager/, "/agent") || "/agent";
  if (target === "/agent" || target === "/agent/" || target === "/agent/home") {
    target = "/agent/dashboard";
  } else if (target === "/agent/maintenance") {
    target = "/agent/jobs";
  }
  return <Navigate to={target + search + hash} replace />;
};

const PublicOnlyRoute = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const { state } = useLocation();
  const from: string | undefined = state?.from;
  let userRoute: string | undefined = undefined;

  switch (user?.profile?.UserType) {
    case UserType.Agent:
      userRoute = "/agent/dashboard";
      break;
    // Console-first: agency managers land on the console, not the
    // marketplace workspace (mirrors IndexRoute). Platform admins go there
    // too — it's their only cross-agency surface, and the agency picker
    // lives in it.
    case UserType.AgencyManager: // falls through
    case UserType.Admin:
      userRoute = "/pm";
      break;
    // Renter login is retired; a Tenant-typed session lands on the sunset
    // page that points them to WhatsApp.
    case UserType.Tenant:
      userRoute = "/tenant";
      break;
    case UserType.Landlord:
      userRoute = "/landlord/home";
      break;
    // Managers live on the shared management surface.
    case UserType.Manager:
      userRoute = "/agent/dashboard";
      break;
    case UserType.Unspecified:
      userRoute = "/user/select-role";
      break;
  }

  // Only navigate to either the default user route or the from route if there is a user logged in
  if (userRoute) {
    return <Navigate to={from || userRoute} />;
  }

  return children;
};

const ReportsPageRoute = lazy(
  () => import("./pages/Shared/Reports/ReportsPage"),
);
const AppReportPageRoute = lazy(
  () => import("./pages/Shared/Reports/ReportPage"),
);
const SavedViewPageRoute = lazy(
  () => import("./pages/Shared/Reports/SavedViewPage"),
);

const ProtectedRoute = ({
  children,
  expectedUserTypes,
  loginUserType,
}: {
  children: ReactNode;
  expectedUserTypes: UserType[];
  loginUserType: UserType;
}) => {
  const { isAuthenticated, login, user } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    console.log("Logging in from ProtectedRoute", isAuthenticated);
    login(location.pathname + location.search, loginUserType);
    return null;
  }

  if (!expectedUserTypes.includes(user?.profile?.UserType as UserType)) {
    // A link made for the other workspace (a notice to someone who owns one
    // property and runs another, a link sent before their account switched)
    // opens the same page in the account's own, else its home.
    const ownPage = pageInOwnWorkspace(location, user?.profile?.UserType);
    if (ownPage) return <Navigate to={ownPage} replace />;
    console.warn(
      `User type ${user?.profile?.UserType} is not allowed to access this route. Expected one of: ${expectedUserTypes.join(
        ", ",
      )}`,
    );
    return <Navigate to="/" replace />;
  }

  return children;
};

const IndexRoute = () => {
  const { user, isAuthenticated, login } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    login(location.pathname + location.search);
    return null;
  }

  switch (user?.profile?.UserType) {
    case UserType.Agent:
      return <Navigate to="/agent/dashboard" replace />;
    // Console-first (2026-07-14): agency managers land on the Hazel console —
    // external brokerages don't use the marketplace app yet, and their
    // managers' product IS the console. The /agent workspace stays reachable
    // by URL for rentinit's own manager.
    // Platform admins land here as well: the console is their only
    // cross-agency surface, and the agency picker lives in it.
    case UserType.AgencyManager: // falls through
    case UserType.Admin:
      return <Navigate to="/pm" replace />;
    // Renter login is retired; a Tenant-typed session lands on the sunset
    // page that points them to WhatsApp.
    case UserType.Tenant:
      return <Navigate to="/tenant" replace />;
    case UserType.Unspecified:
      return <Navigate to="/user/select-role" replace />;
    case UserType.Landlord:
      return <Navigate to="/landlord/home" replace />;
    // Managers live on the shared management surface. Before 2026-09-01 this
    // case was missing and a signed-in Manager only landed by bouncing
    // through /signin's PublicOnlyRoute.
    case UserType.Manager:
      return <Navigate to="/agent/dashboard" replace />;
    default:
      return <Navigate to="/signin" />;
  }
};
